/**
 * Secure Access → Access Gateways → detail (design §10.4, §7.2).
 *
 * Five tabs (VSNP-1220): **Overview** (identity, certificate, node-reported
 * spec, host load, configuration, danger zone) · **Traffic** · **Sessions** ·
 * **Security** · **Logs**.
 *
 * The tab is in the URL (`?tab=traffic`) so a link to "this gateway's denies"
 * is a link someone can paste into a ticket. Each tab's panels mount only when
 * the tab is shown — a detail page that fires twenty PromQL queries on open,
 * nineteen of them invisible, is how a monitoring UI becomes the load it is
 * supposed to measure.
 */

import { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { ArrowLeft, CheckCircle2, RefreshCw, Server } from 'lucide-react';
import { z } from 'zod';
import StatusBadge from '../../components/common/StatusBadge';
import ConfirmDialog from '../../components/common/ConfirmDialog';
import ZtnaChip from '../../components/access/ZtnaChip';
import AccessFormField from '../../components/access/AccessFormField';
import { CertificateCard, DeviceSpecCard, Field, MintTokenSection } from '../../components/access/NodeDetailBits';
import GatewayTenantsCard from '../../components/access/GatewayTenantsCard';
import { fmtDate, nodeStatusLabel } from '../../components/access/nodeStatus';
import { QueryChart, QueryStat, QueryTable } from '../../components/observability/QueryPanels';
import { StateTimeline } from '../../components/observability/StateTimeline';
import { SessionsTable } from '../../components/observability/SessionsTable';
import { AuditEventsTable } from '../../components/observability/AuditEventsTable';
import { NodeLogsPanel } from '../../components/observability/NodeLogsPanel';
import { LogLevelCell } from '../../components/observability/MetricCells';
import { TimeRange } from '../../components/observability/TimeRange';
import { rangeDuration, rangeLabel, DEFAULT_TIME_RANGE, resolveTimeRange, type TimeRangeValue } from '../../utils/timeRange';
import { NODE_IFACE_FILTER, NODE_IFACE_FILTER_LABEL } from '../../utils/obsChart';
import { useToast } from '../../components/common/ToastContainer';
import { zodResolver } from '../../utils/zodResolver';
import accessService, { type AccessGateway } from '../../services/access.service';

const TABS = ['overview', 'traffic', 'sessions', 'security', 'logs'] as const;
type Tab = (typeof TABS)[number];

const configSchema = z.object({
  name: z.string().min(1, 'Name is required').max(255),
  popCode: z.string().max(32).optional(),
  region: z.string().max(64).optional(),
  fqdn: z.string().max(255).optional(),
  publicEndpoint: z.string().max(255).optional(),
  quicPort: z.coerce.number().int().min(1).max(65535),
  maxSessions: z.coerce.number().int().min(1).max(1000000),
  releaseChannel: z.enum(['stable', 'canary', 'beta']),
  rolloutRing: z.coerce.number().int().min(0).max(10),
});

type ConfigForm = z.infer<typeof configSchema>;

/*
 * Every query below narrows with `node="$node"`; the proxy still injects the
 * caller's node allow-list into the same selector, so a node id from another
 * tenant returns nothing instead of that node's load.
 */
const METRIC_ONLY_TAG = {
  key: 'metrics-only',
  label: 'metrics only',
  title:
    'Reported only by the gateway\'s metrics (agw_component_up). It is not in the status heartbeat, ' +
    'so it is not part of the Health shown in the gateway list and the API.',
};

/**
 * Host load and component health for one gateway (design §7.2 Overview).
 *
 * `heartbeat` is the component list of the last status heartbeat
 * (last_components) — the list the gateway's Health is rolled up from. The
 * timeline is the METRICS view, which reports more components (envoy, l3gw,
 * xdp on dev-1). Those are labelled "metrics only" rather than merged or
 * dropped (VSNP-1438): merging would make Health depend on the metrics plane,
 * dropping would hide real data. The tag is derived from the heartbeat, so it
 * disappears by itself once the gateway reports every component there.
 */
const GatewayOverviewPanels = ({
  node,
  range,
  heartbeat,
}: {
  node: string;
  range: TimeRangeValue;
  heartbeat: string[] | null;
}) => (
  <>
    <div className="grid grid-cols-2 gap-4 md:grid-cols-4">
      <QueryStat
        title="Live sessions"
        query={`sum(agw_sessions_live{node="${node}"})`}
        testId="gw-stat-sessions"
        hint="clients + connectors"
      />
      <QueryStat title="Active flows" query={`sum(agw_flows_active{node="${node}"})`} testId="gw-stat-flows" />
      <QueryStat
        title="Bundle epoch"
        query={`max(agw_policy_bundle_epoch{node="${node}"})`}
        format={(v) => (v === null ? '—' : String(v))}
        testId="gw-stat-epoch"
        hint="policy applied on the node"
      />
      <QueryStat
        title="Audit spool disk"
        query={`100 * max(node_filesystem_used_bytes{node="${node}", mount=~"/var/lib/securelink|/"} / node_filesystem_size_bytes)`}
        format={(v) => (v === null ? '—' : `${v.toFixed(1)}%`)}
        testId="gw-stat-disk"
        hint="audit mount, falls back to /"
      />
    </div>

    <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
      {/*
        A state timeline, not a line chart (VSNP-1261). agw_component_up is a
        0/1 series: the y-axis meant nothing, a component that was down for the
        whole window was dropped from the chart entirely (MetricsChart filters
        all-zero series), and a gap in the samples was drawn as a straight line
        through it. A component with no series at all is ABSENT, not zero —
        design §9a — so it simply has no band.
      */}
      <StateTimeline
        title="Component health"
        query={`agw_component_up{node="${node}"}`}
        range={range}
        labelKeys={['component']}
        emptyMessage="This gateway has reported no component health in this range."
        testId="gw-chart-components"
        bandTag={(name) => (heartbeat && !heartbeat.includes(name) ? METRIC_ONLY_TAG : null)}
        footnote={
          heartbeat && heartbeat.length > 0 ? (
            <>
              Health in the gateway list and the API is rolled up from the status heartbeat&apos;s components
              ({heartbeat.join(', ')}). Components marked <em>metrics only</em> are reported by the metrics alone.
            </>
          ) : (
            'The last status heartbeat carried no component list, so which of these feed Health is not known.'
          )
        }
      />
      <QueryChart
        title="CPU usage"
        query={`node_cpu_usage_percent{node="${node}"}`}
        range={range}
        unit="%"
        testId="gw-chart-cpu"
      />
      <QueryChart
        title="Memory used"
        query={`100 * node_memory_used_bytes{node="${node}"} / node_memory_total_bytes`}
        range={range}
        unit="%"
        testId="gw-chart-mem"
      />
      <QueryChart
        title="Filesystem used"
        query={`100 * node_filesystem_used_bytes{node="${node}"} / node_filesystem_size_bytes`}
        range={range}
        labelKeys={['mount']}
        unit="%"
        testId="gw-chart-disk"
      />
    </div>
  </>
);

/** Traffic tab (design §7.2). */
const GatewayTrafficTab = ({ node, range }: { node: string; range: TimeRangeValue }) => {
  // Every rate on this tab uses the PICKER's window (doc 22 §10.4). A 30-day
  // view plotted with a fixed 5-minute rate is both misleading and needlessly
  // spiky — each point then describes five minutes the chart does not show.
  const win = resolveTimeRange(range).window;
  return (
  <div className="space-y-4">
    <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
      <QueryChart
        title={`Network receive (Mbps, ${NODE_IFACE_FILTER_LABEL})`}
        query={`rate(node_network_receive_bytes_total{node="${node}", ${NODE_IFACE_FILTER}}[${win}]) * 8 / 1000000`}
        range={range}
        labelKeys={['iface']}
        testId="gw-chart-rx"
      />
      <QueryChart
        title={`Network transmit (Mbps, ${NODE_IFACE_FILTER_LABEL})`}
        query={`rate(node_network_transmit_bytes_total{node="${node}", ${NODE_IFACE_FILTER}}[${win}]) * 8 / 1000000`}
        range={range}
        labelKeys={['iface']}
        testId="gw-chart-tx"
      />
      <QueryChart
        title="Flows active by app"
        query={`agw_flows_active{node="${node}"}`}
        range={range}
        labelKeys={['app', 'layer']}
        testId="gw-chart-flows"
      />
      <QueryChart
        title="Decisions per second"
        query={`sum by (action) (rate(agw_flow_decisions_total{node="${node}"}[${win}]))`}
        range={range}
        labelKeys={['action']}
        colorByAction
        testId="gw-chart-decisions"
      />
      <QueryChart
        title="Bytes per second by app (top 10)"
        query={`topk(10, sum by (app, direction) (rate(agw_flow_bytes_total{node="${node}"}[${win}])))`}
        range={range}
        labelKeys={['app', 'direction']}
        unit=" B/s"
        testId="gw-chart-appbytes"
      />
      <div className="space-y-4">
        <QueryTable
          title="Connector sessions by app"
          query={`agw_connector_sessions{node="${node}"}`}
          labelKey="app"
          valueLabel="sessions"
          emptyMessage="No connector sessions on this gateway."
          testId="gw-connector-sessions"
        />
        <QueryTable
          title="Apps with no connector"
          query={`agw_connector_app_unserved{node="${node}"} == 1`}
          labelKey="app"
          valueLabel="unserved"
          alertOnOne
          emptyMessage="Every app has at least one connector."
          testId="gw-unserved"
        />
      </div>
    </div>
  </div>
  );
};

/** Sessions tab (design §7.2): the metrics view and the record view together. */
const GatewaySessionsTab = ({ node, gatewayId, range }: { node: string; gatewayId: string; range: TimeRangeValue }) => {
  // increase() over the SELECTED window, as design §7.2 specifies ($range).
  //
  // `rangeDuration`, not the picker's `window`: the two are different numbers
  // and the difference is the point. `window` is the RATE window — a fraction
  // of the range, chosen so a line stays smooth — while a rejection count wants
  // the TOTAL over the range. Doc 22 §10.4 is about panels that used neither.
  const total = rangeDuration(resolveTimeRange(range));
  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        <QueryChart
          title="Sessions live by kind"
          query={`agw_sessions_live{node="${node}"}`}
          range={range}
          labelKeys={['session_kind']}
          testId="gw-chart-sessions"
        />
        <QueryChart
          title={`Rejections by reason (${rangeLabel(range)})`}
          query={`sum by (reason) (increase(agw_sessions_rejected_total{node="${node}"}[${total}]))`}
          range={range}
          labelKeys={['reason']}
          testId="gw-chart-rejects"
        />
      </div>
      <SessionsTable range={range} gatewayId={gatewayId} showNode={false} title="Sessions on this gateway" />
    </div>
  );
};

const GatewayDetail = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const toast = useToast();

  const [searchParams, setSearchParams] = useSearchParams();
  const tabParam = searchParams.get('tab');
  const tab: Tab = (TABS as readonly string[]).includes(tabParam ?? '') ? (tabParam as Tab) : 'overview';

  const [gateway, setGateway] = useState<AccessGateway | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [decommissionOpen, setDecommissionOpen] = useState(false);
  const [decommissioning, setDecommissioning] = useState(false);
  const [range, setRange] = useState<TimeRangeValue>(DEFAULT_TIME_RANGE);

  const form = useForm<ConfigForm>({ resolver: zodResolver<ConfigForm>(configSchema) });
  const { reset } = form;

  const fetchGateway = useCallback(async () => {
    if (!id) return;
    try {
      setLoading(true);
      setError(null);
      const gw = await accessService.getGateway(id);
      setGateway(gw);
      reset({
        name: gw.name ?? '',
        popCode: gw.pop_code ?? '',
        region: gw.region ?? '',
        fqdn: gw.fqdn ?? '',
        publicEndpoint: gw.public_endpoint ?? '',
        quicPort: gw.quic_port,
        maxSessions: gw.max_sessions,
        releaseChannel: gw.release_channel,
        rolloutRing: gw.rollout_ring,
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load the gateway');
    } finally {
      setLoading(false);
    }
  }, [id, reset]);

  useEffect(() => {
    void fetchGateway();
  }, [fetchGateway]);

  const saveConfig = form.handleSubmit(async (values) => {
    if (!gateway) return;
    try {
      await accessService.updateGateway(gateway.id, {
        name: values.name,
        popCode: values.popCode || null,
        region: values.region || null,
        fqdn: values.fqdn || null,
        publicEndpoint: values.publicEndpoint || null,
        quicPort: values.quicPort,
        maxSessions: values.maxSessions,
        releaseChannel: values.releaseChannel,
        rolloutRing: values.rolloutRing,
      });
      toast.success('Configuration saved', gateway.name ?? gateway.node_id);
      await fetchGateway();
    } catch (err) {
      toast.error('Save failed', err instanceof Error ? err.message : 'Could not save the configuration');
    }
  });

  const handleApprove = async () => {
    if (!gateway) return;
    try {
      await accessService.approveGateway(gateway.id);
      toast.success('Gateway approved', gateway.name ?? gateway.node_id);
      await fetchGateway();
    } catch (err) {
      toast.error('Approve failed', err instanceof Error ? err.message : 'Could not approve the gateway');
    }
  };

  const handleDecommission = async () => {
    if (!gateway) return;
    try {
      setDecommissioning(true);
      await accessService.decommissionGateway(gateway.id);
      toast.success('Gateway decommissioned', gateway.name ?? gateway.node_id);
      navigate('/access/gateways');
    } catch (err) {
      toast.error('Decommission failed', err instanceof Error ? err.message : 'Could not decommission the gateway');
    } finally {
      setDecommissioning(false);
    }
  };

  if (loading && !gateway) {
    return <div className="py-16 text-center text-gray-500">Loading gateway…</div>;
  }
  if (error || !gateway) {
    return (
      <div className="space-y-4">
        <Link to="/access/gateways" className="inline-flex items-center gap-1 text-sm text-primary-600 hover:underline">
          <ArrowLeft className="h-4 w-4" /> Access Gateways
        </Link>
        <div className="rounded-md border border-danger-200 bg-danger-50 px-4 py-3 text-sm text-danger-700">
          {error ?? 'Gateway not found'}
        </div>
      </div>
    );
  }

  const awaitingEnrolment = !gateway.certificate_serial_number && gateway.status !== 'decommissioned';

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between">
        <div>
          <Link to="/access/gateways" className="inline-flex items-center gap-1 text-sm text-primary-600 hover:underline">
            <ArrowLeft className="h-4 w-4" /> Access Gateways
          </Link>
          <div className="mt-1 flex items-center gap-3">
            <h1 className="text-3xl font-bold text-gray-900">{gateway.name ?? gateway.node_id}</h1>
            <ZtnaChip />
            <StatusBadge status={nodeStatusLabel(gateway.status, gateway.certificate_serial_number)} />
          </div>
          <p className="mt-1 font-mono text-xs text-gray-500">{gateway.node_id}</p>
        </div>
        <div className="flex items-center gap-2">
          <TimeRange value={range} onChange={setRange} />
          <button type="button" className="btn-secondary" onClick={() => void fetchGateway()} disabled={loading}>
            <RefreshCw className={`mr-2 inline h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </button>
          {gateway.status === 'pending_approval' && gateway.certificate_serial_number && (
            <button type="button" className="btn-primary" onClick={() => void handleApprove()}>
              <CheckCircle2 className="mr-2 inline h-4 w-4" />
              Approve
            </button>
          )}
        </div>
      </div>

      {awaitingEnrolment && (
        <div className="card border-teal-200 bg-teal-50/40">
          <div className="flex items-center justify-between gap-4">
            <div>
              <h2 className="font-semibold text-gray-900">Awaiting enrolment</h2>
              <p className="mt-0.5 text-sm text-gray-600">
                This gateway is registered but has not enrolled. Generate a one-time token and run the
                bootstrap on the PoP host — the row flips live when the node completes the exchange.
              </p>
            </div>
            <MintTokenSection kind="gateway" nodeRowId={gateway.id} nodeName={gateway.name ?? gateway.node_id} nodeId={gateway.node_id} />
          </div>
        </div>
      )}

      {/* Tabs (design §7.2) */}
      <div className="border-b border-gray-200">
        <nav className="-mb-px flex space-x-8">
          {TABS.map((t) => (
            <button
              key={t}
              type="button"
              data-testid={`gw-tab-${t}`}
              onClick={() => setSearchParams(t === 'overview' ? {} : { tab: t }, { replace: true })}
              className={`border-b-2 px-1 py-3 text-sm font-medium capitalize ${
                tab === t
                  ? 'border-primary-500 text-primary-600'
                  : 'border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700'
              }`}
            >
              {t}
            </button>
          ))}
        </nav>
      </div>

      {tab === 'traffic' && <GatewayTrafficTab node={gateway.node_id} range={range} />}
      {tab === 'sessions' && <GatewaySessionsTab node={gateway.node_id} gatewayId={gateway.id} range={range} />}
      {tab === 'security' && (
        <AuditEventsTable
          range={range}
          preset="security"
          gatewayId={gateway.id}
          title={`Security events — ${gateway.name ?? gateway.node_id}`}
          showFacets
          testId="gw-security"
        />
      )}
      {tab === 'logs' && <NodeLogsPanel node={gateway.node_id} range={range} />}

      {tab === 'overview' && (
      <>
      {/* Host load (design §7.2 Overview) */}
      <GatewayOverviewPanels
        node={gateway.node_id}
        range={range}
        heartbeat={gateway.last_components ? gateway.last_components.map((c) => c.name) : null}
      />

      {/* Overview */}
      <div className="card">
        <h2 className="mb-3 flex items-center gap-2 text-lg font-semibold text-gray-900">
          <Server className="h-4 w-4 text-teal-600" /> Overview
        </h2>
        <div className="grid grid-cols-2 gap-x-6 gap-y-2 md:grid-cols-4">
          <Field label="Node ID" value={gateway.node_id} mono />
          <Field label="PoP / Region" value={[gateway.pop_code, gateway.region].filter(Boolean).join(' · ')} />
          <Field label="Version" value={gateway.version} />
          <Field label="Health" value={gateway.last_health} />
          <Field label="Registered" value={fmtDate(gateway.registered_at)} />
          <Field label="Last seen" value={fmtDate(gateway.last_seen)} />
          <Field label="Sessions" value={gateway.last_session_count ?? 0} />
          <Field label="Config epoch" value={gateway.config_epoch} />
          <Field label="License" value={gateway.sharing_mode} />
          {/* VSNP-1338: what the node IS running at, beside what we asked for. */}
          <div data-testid="gw-log-level">
            <div className="text-xs text-gray-500">Log level</div>
            <div className="text-sm text-gray-900">
              <LogLevelCell
                effective={gateway.effective_log_level}
                intent={gateway.log_level}
                override={gateway.log_level_override}
                since={gateway.effective_log_level_since}
              />
            </div>
          </div>
          <Field label="Log level intent" value={gateway.log_level ?? 'unset (node default: info)'} />
        </div>
      </div>

      {/* Tenancy (VSNP-1126): owner line for dedicated, assignment management for shared */}
      <GatewayTenantsCard gateway={gateway} onChanged={() => void fetchGateway()} />

      <CertificateCard
        serial={gateway.certificate_serial_number}
        issuedAt={gateway.certificate_issued_at}
        expiresAt={gateway.certificate_expires_at}
      />

      <DeviceSpecCard spec={gateway.device_spec} />

      {/* Configuration — the tuning fields that moved out of registration */}
      <div className="card">
        <h2 className="mb-3 text-lg font-semibold text-gray-900">Configuration</h2>
        <div className="grid grid-cols-2 gap-4 md:grid-cols-3">
          <AccessFormField label="Name" htmlFor="gwd-name" required error={form.formState.errors.name?.message}>
            <input id="gwd-name" className="input-base w-full" {...form.register('name')} />
          </AccessFormField>
          <AccessFormField label="PoP Code" htmlFor="gwd-pop" error={form.formState.errors.popCode?.message}>
            <input id="gwd-pop" className="input-base w-full" {...form.register('popCode')} />
          </AccessFormField>
          <AccessFormField label="Region" htmlFor="gwd-region" error={form.formState.errors.region?.message}>
            <input id="gwd-region" className="input-base w-full" {...form.register('region')} />
          </AccessFormField>
          <AccessFormField label="Public Endpoint" htmlFor="gwd-endpoint" error={form.formState.errors.publicEndpoint?.message} hint="Host clients dial.">
            <input id="gwd-endpoint" className="input-base w-full" {...form.register('publicEndpoint')} />
          </AccessFormField>
          <AccessFormField label="QUIC Port" htmlFor="gwd-port" error={form.formState.errors.quicPort?.message}>
            <input id="gwd-port" type="number" className="input-base w-full" {...form.register('quicPort')} />
          </AccessFormField>
          <AccessFormField label="FQDN" htmlFor="gwd-fqdn" error={form.formState.errors.fqdn?.message}>
            <input id="gwd-fqdn" className="input-base w-full" {...form.register('fqdn')} />
          </AccessFormField>
          <AccessFormField label="Max Sessions" htmlFor="gwd-sessions" error={form.formState.errors.maxSessions?.message} hint="Capacity tuning.">
            <input id="gwd-sessions" type="number" className="input-base w-full" {...form.register('maxSessions')} />
          </AccessFormField>
          <AccessFormField label="Release Channel" htmlFor="gwd-channel" error={form.formState.errors.releaseChannel?.message} hint="Upgrade policy.">
            <select id="gwd-channel" className="input-base w-full" {...form.register('releaseChannel')}>
              <option value="stable">stable</option>
              <option value="canary">canary</option>
              <option value="beta">beta</option>
            </select>
          </AccessFormField>
          <AccessFormField label="Rollout Ring" htmlFor="gwd-ring" error={form.formState.errors.rolloutRing?.message}>
            <input id="gwd-ring" type="number" className="input-base w-full" {...form.register('rolloutRing')} />
          </AccessFormField>
        </div>
        <div className="mt-4 flex justify-end">
          <button type="button" className="btn-primary" onClick={saveConfig} disabled={form.formState.isSubmitting}>
            {form.formState.isSubmitting ? 'Saving…' : 'Save configuration'}
          </button>
        </div>
      </div>

      {/* Danger zone */}
      {gateway.status !== 'decommissioned' && (
        <div className="card border-danger-200">
          <h2 className="mb-2 text-lg font-semibold text-danger-700">Danger zone</h2>
          <div className="flex items-center justify-between gap-4">
            <p className="text-sm text-gray-600">
              Decommissioning stops clients being brokered through this PoP and revokes its standing.
              The record and its history are kept.
            </p>
            <button type="button" className="btn-danger" onClick={() => setDecommissionOpen(true)}>
              Decommission
            </button>
          </div>
        </div>
      )}
      </>
      )}

      <ConfirmDialog
        isOpen={decommissionOpen}
        onClose={() => setDecommissionOpen(false)}
        onConfirm={handleDecommission}
        title="Decommission gateway"
        message={`Decommission '${gateway.name ?? gateway.node_id}'? Clients will stop being brokered through this PoP.`}
        confirmText="Decommission"
        variant="danger"
        isLoading={decommissioning}
      />
    </div>
  );
};

export default GatewayDetail;
